import { useEffect, useState } from "react";
import { List } from "lucide-react";

export type TocItem = { id: string; text: string; level: 2 | 3 };

type Props = { items: TocItem[] };

export function TableOfContents({ items }: Props) {
  const [active, setActive] = useState<string>("");

  useEffect(() => {
    if (typeof window === "undefined" || !items.length) return;
    const nodes = items
      .map((it) => document.getElementById(it.id))
      .filter((n): n is HTMLElement => !!n);
    if (!nodes.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: [0, 1] },
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [items]);

  if (!items.length) return null;

  return (
    <aside
      aria-label="Table of contents"
      className="not-prose mb-8 rounded-2xl border border-border bg-gradient-to-br from-card to-muted/30 p-5 shadow-[0_1px_0_rgba(10,25,47,0.04)]"
    >
      <details open className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-foreground marker:hidden">
          <List aria-hidden="true" className="h-4 w-4 text-accent" />
          In this article
          <span className="ml-auto text-[10px] font-medium text-muted-foreground group-open:hidden">Show</span>
          <span className="ml-auto hidden text-[10px] font-medium text-muted-foreground group-open:inline">Hide</span>
        </summary>
        <nav className="mt-4">
          <ol className="space-y-1.5 text-sm">
            {items.map((it) => (
              <li key={it.id} className={it.level === 3 ? "pl-4" : ""}>
                <a title={`Jump to section: ${it.text}`}
                  href={`#${it.id}`}
                  className={`block rounded-md border-l-2 py-1 pl-3 pr-2 transition-colors ${
                    active === it.id
                      ? "border-accent bg-accent/5 font-semibold text-foreground"
                      : "border-transparent text-muted-foreground hover:border-accent/40 hover:text-foreground"
                  }`}
                >
                  {it.text}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </details>
    </aside>
  );
}

export default TableOfContents;
